/**
 * Session Report Service
 * 
 * Handles updating session reports with photos and check-in responses
 * from Chef reminders during live sessions.
 */

class SessionReportService {
    constructor() {
        this.currentSessionId = null;
        this.reportData = {
            photos: [],
            checkIns: [],
            reminders: [],
            aiInteractions: {
                tips: [],
                chefQuestions: [],
                voiceProcessing: []
            }
        };
    }

    /**
     * Initialize session report for a new session
     */
    initializeSession(sessionId) {
        this.currentSessionId = sessionId;
        this.reportData = {
            photos: [],
            checkIns: [],
            reminders: [],
            aiInteractions: {
                tips: [],
                chefQuestions: [],
                voiceProcessing: []
            }
        };
        // console.log('📊 Session report initialized for session:', sessionId);
    }

    /**
     * Add a photo to the session report
     */
    async addPhoto(photoData, reminder) {
        if (!this.currentSessionId) {
            console.warn('No active session for photo report');
            return;
        }

        try {
            // Convert blob to base64 for storage
            const base64Data = await this.convertBlobToBase64(photoData.blob);
            
            const photoEntry = {
                id: photoData.id,
                timestamp: photoData.timestamp,
                stepIndex: photoData.stepIndex,
                trigger: photoData.trigger,
                reminderMessage: photoData.reminderMessage,
                imageData: base64Data,
                size: photoData.size,
                type: photoData.type
            };

            this.reportData.photos.push(photoEntry);
            
            // Record the reminder that triggered this photo
            this.recordReminder(reminder, 'photo_captured');
            
            // console.log('📸 Photo added to session report:', photoEntry.id);
            
            // Emit event for other services
            this.emitPhotoAdded(photoEntry);
            
            return photoEntry;
            
        } catch (error) {
            console.error('Error adding photo to session report:', error);
            throw error;
        }
    }

    /**
     * Add a check-in response to the session report
     */
    addCheckIn(checkInData, reminder) {
        if (!this.currentSessionId) {
            console.warn('No active session for check-in report');
            return;
        }

        const checkInEntry = {
            id: `checkin_${Date.now()}`,
            timestamp: new Date().toISOString(),
            stepIndex: checkInData.stepIndex,
            trigger: checkInData.trigger,
            reminderMessage: checkInData.reminderMessage,
            response: checkInData.response,
            responseLength: checkInData.response.length
        };

        this.reportData.checkIns.push(checkInEntry);
        
        // Record the reminder that triggered this check-in
        this.recordReminder(reminder, 'checkin_responded');
        
        // console.log('💬 Check-in added to session report:', checkInEntry.id);
        
        // Emit event for other services
        this.emitCheckInAdded(checkInEntry);
        
        return checkInEntry;
    }

    /**
     * Record a reminder event
     */
    recordReminder(reminder, outcome) {
        const reminderEntry = {
            id: `reminder_${Date.now()}`,
            timestamp: new Date().toISOString(),
            type: reminder.type,
            trigger: reminder.trigger,
            message: reminder.message,
            stepIndex: reminder.stepIndex,
            outcome: outcome // 'dismissed', 'photo_captured', 'checkin_responded'
        };

        this.reportData.reminders.push(reminderEntry);
        
        // console.log('🔔 Reminder recorded:', reminderEntry.id, outcome);
    }

    /**
     * Get current session report data
     */
    getReportData() {
        return {
            sessionId: this.currentSessionId,
            ...this.reportData,
            summary: {
                totalPhotos: this.reportData.photos.length,
                totalCheckIns: this.reportData.checkIns.length,
                totalReminders: this.reportData.reminders.length,
                reminderOutcomes: this.getReminderOutcomes()
            }
        };
    }

    /**
     * Get reminder outcome statistics
     */
    getReminderOutcomes() {
        const outcomes = {};
        this.reportData.reminders.forEach(reminder => {
            outcomes[reminder.outcome] = (outcomes[reminder.outcome] || 0) + 1;
        });
        return outcomes;
    }

    /**
     * Convert blob to base64
     */
    async convertBlobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Emit photo added event
     */
    emitPhotoAdded(photoEntry) {
        // This could be used to notify other services or update the UI
        const event = new CustomEvent('sessionReport:photoAdded', {
            detail: photoEntry
        });
        window.dispatchEvent(event);
    }

    /**
     * Emit check-in added event
     */
    emitCheckInAdded(checkInEntry) {
        // This could be used to notify other services or update the UI
        const event = new CustomEvent('sessionReport:checkInAdded', {
            detail: checkInEntry
        });
        window.dispatchEvent(event);
    }

    /**
     * Clear session report data
     */
    clearReport() {
        this.currentSessionId = null;
        this.reportData = {
            photos: [],
            checkIns: [],
            reminders: [],
            aiInteractions: {
                tips: [],
                chefQuestions: [],
                voiceProcessing: []
            }
        };
        // console.log('📊 Session report cleared');
    }

    /**
     * Add AI tip interaction to session report
     */
    addTipInteraction(tipData, stepIndex, feedback = null) {
        if (!this.currentSessionId) {
            console.warn('No active session for tip interaction');
            return;
        }

        const tipInteraction = {
            id: `tip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            stepIndex,
            tipId: tipData.id,
            tipContent: tipData.content,
            tipType: tipData.type,
            tipPriority: tipData.priority,
            sourceContext: tipData.sourceContext,
            feedback, // 'helpful', 'not_helpful', or null
            shownAt: new Date().toISOString()
        };

        this.reportData.aiInteractions.tips.push(tipInteraction);
        console.log('📊 Tip interaction recorded:', tipInteraction);
    }

    /**
     * Add Chef question/answer interaction to session report
     */
    addChefQuestionInteraction(question, response, stepIndex, context = {}) {
        if (!this.currentSessionId) {
            console.warn('No active session for Chef question interaction');
            return;
        }

        const qaInteraction = {
            id: `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            stepIndex,
            question,
            response,
            context,
            phase: context.phase || 'active'
        };

        this.reportData.aiInteractions.chefQuestions.push(qaInteraction);
        console.log('📊 Chef Q&A interaction recorded:', qaInteraction);
    }

    /**
     * Add voice processing interaction to session report
     */
    addVoiceProcessingInteraction(originalTranscript, processedTranscript, stepIndex, context = {}) {
        if (!this.currentSessionId) {
            console.warn('No active session for voice processing interaction');
            return;
        }

        const voiceInteraction = {
            id: `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            stepIndex,
            originalTranscript,
            processedTranscript,
            context,
            phase: context.phase || 'active'
        };

        this.reportData.aiInteractions.voiceProcessing.push(voiceInteraction);
        console.log('📊 Voice processing interaction recorded:', voiceInteraction);
    }

    /**
     * Update tip feedback
     */
    updateTipFeedback(tipId, feedback) {
        if (!this.currentSessionId) {
            console.warn('No active session for tip feedback update');
            return;
        }

        const tipInteraction = this.reportData.aiInteractions.tips.find(tip => tip.tipId === tipId);
        if (tipInteraction) {
            tipInteraction.feedback = feedback;
            tipInteraction.feedbackTimestamp = new Date().toISOString();
            console.log('📊 Tip feedback updated:', { tipId, feedback });
        } else {
            console.warn('Tip interaction not found for feedback update:', tipId);
        }
    }

    /**
     * Export session report data
     */
    exportReport() {
        return {
            sessionId: this.currentSessionId,
            exportedAt: new Date().toISOString(),
            ...this.getReportData()
        };
    }
}

// Create singleton instance
const sessionReport = new SessionReportService();

export default sessionReport;
